import Box from '@mui/material/Box';
import { map, nth, split } from 'lodash';
import { useAuthenticated, useRecordSelection, useUpdate } from 'react-admin';
import {
    Datagrid,
    DateField,
    FunctionField,
    Identifier,
    List,
    ReferenceField,
    ReferenceOneField,
    SelectInput,
    TextField,
    TextInput,
    useNotify,
    usePermissions,
    useTranslate,
    useUnselectAll,
} from 'react-admin';
import { useParams } from 'react-router';
import {
    AUDIT_METADATA_AVATAR_TYPE_NAME,
    AUDIT_STATE_AUDIT,
    AUDIT_STATE_CANCELED,
    AUDIT_STATE_DONE,
    AUDIT_STATE_TAKE_INTO_ACCOUNT,
    AUDIT_STATUS_NODES_LIST,
    AVATARS,
    GROUP_AVATARS,
    GROUP_AVATAR_SITE_PREFIX,
    INTERVENTION_METADATA_AVATAR_TYPE_NAME,
    ROLE_BIG_UPDATE_AUDIT,
} from '@/utils/CONST';
import tableRowSx from '@/utils/table-row-sx';
import { Avatar } from '@/utils/types';
import ChangeStatusField from '@/components/common/change-status-field/change-status-field';
import CustomBlock from '@/components/common/custom-block/custom-block';
import FilterBox from '@/components/common/filters/common/filter-box';
import CreationDateFilter from '@/components/common/filters/creation-date-filter';
import ListStylesContainer from '@/components/common/list-container/list-styles-container';
import LinkToInterventionButton from '@/components/devis/link-to-intervention-button';
import AuditListActions from '@/components/audits/audit-list-actions';

const AuditsList = () => {
    useAuthenticated();

    const { permissions } = usePermissions();
    const translate = useTranslate();
    const { placeId, siteId } = useParams();
    const [selectedAvatarsIds, { select }] = useRecordSelection({
        resource: AVATARS,
        storeKey: 'auditsList',
    });
    const unselectAll = useUnselectAll(AVATARS);
    const [update, { isLoading: isLoadingUpdate }] = useUpdate();
    const notify = useNotify();

    const onRowClick: any = (id: Identifier) => {
        unselectAll();
        select([id]);
    };

    const removeAuditFromGroup = (avatarUriId: string) => {
        update(
            AVATARS,
            { id: avatarUriId, data: { groupAvatars: [] } },
            { onSuccess: () => notify('disorder.actions.change_status.one', { type: 'success' }) },
        );
    };

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: 1,
            }}
        >
            <CustomBlock label={translate('main.navigation.audit')}>
                <ListStylesContainer>
                    <List
                        actions={<AuditListActions />}
                        component='div'
                        resource={AVATARS}
                        filter={{
                            'metadataAvatarType.name': AUDIT_METADATA_AVATAR_TYPE_NAME,
                            parent: placeId,
                            serialization_groups: ['read_avatar_current_node', 'read_node'],
                            enabled: true,
                            ...(siteId && { 'groupAvatars.name': `${GROUP_AVATAR_SITE_PREFIX}${siteId}` }),
                        }}
                        empty={false}
                        storeKey='auditsList'
                        filters={filters}
                        sort={{
                            field: 'creationDate',
                            order: 'DESC',
                        }}
                    >
                        <Datagrid
                            isRowSelectable={(record: Avatar) => {
                                return (
                                    record?.currentNode?.configuration.idForNodeHumanReadable !==
                                    nth(AUDIT_STATUS_NODES_LIST, 0)?.id
                                );
                            }}
                            rowClick={onRowClick}
                            rowSx={tableRowSx(selectedAvatarsIds)}
                            bulkActionButtons={false}
                        >
                            <FunctionField
                                render={({ alphaId }: { alphaId: string }) => split(alphaId, ':')[1]}
                                label={'audit.list.number'}
                                sortable={false}
                            />

                            <DateField
                                // sortable={false}
                                options={{ year: 'numeric', month: '2-digit', day: '2-digit' }}
                                source={'creationDate'}
                                label={'audit.list.creation_date'}
                            />

                            {permissions?.includes(ROLE_BIG_UPDATE_AUDIT) ? (
                                <FunctionField
                                    render={({ currentNode, id }: any) => (
                                        <ChangeStatusField
                                            currentNode={currentNode}
                                            avatarUriId={id}
                                            choices={AUDIT_STATUS_NODES_LIST}
                                            disabled={
                                                isLoadingUpdate ||
                                                !AUDIT_STATUS_NODES_LIST.length ||
                                                nth(AUDIT_STATUS_NODES_LIST, 2)?.id ===
                                                    currentNode?.configuration?.idForNodeHumanReadable
                                            }
                                            callback={(idForNodeHumanReadable: string) =>
                                                nth(AUDIT_STATUS_NODES_LIST, 0)?.id === idForNodeHumanReadable &&
                                                removeAuditFromGroup(id)
                                            }
                                        />
                                    )}
                                    label='intervention.list.status'
                                />
                            ) : (
                                <TextField source='currentNode.name' label='intervention.list.status' />
                            )}

                            <ReferenceField
                                label={'devis.intervention'}
                                reference={GROUP_AVATARS}
                                source={'groupAvatars'}
                            >
                                {/*<FunctionField*/}
                                {/*    render={(item: any) => {*/}
                                {/*        console.log('ASDASD', item);*/}
                                {/*        return <>asdasd</>;*/}
                                {/*    }}*/}
                                {/*/>*/}
                                <ReferenceOneField
                                    reference={AVATARS}
                                    target={'groupAvatars.id'}
                                    filter={{
                                        metadataAvatarTypeEName: [INTERVENTION_METADATA_AVATAR_TYPE_NAME],
                                        serialization_groups: ['read_avatar_parent'],
                                        // groupAvatars: '/api/group_avatars/67dac8cb1a248',
                                    }}
                                >
                                    <FunctionField
                                        render={(record: Avatar) => <LinkToInterventionButton intervention={record} />}
                                    />
                                </ReferenceOneField>
                            </ReferenceField>
                        </Datagrid>
                    </List>
                </ListStylesContainer>
            </CustomBlock>
        </Box>
    );
};

const filters = [
    <CreationDateFilter source='avatarCreationDate' alwaysOn />,

    <FilterBox source={'audits_filters1'} label={'intervention.filters.label'} alwaysOn>
        <TextInput
            source={'alphaId'}
            parse={(v) => (!!v ? `D:${v}` : '')}
            format={(v) => split(v, ':')[1]}
            name={'alphaId'}
            label='audit.list.number'
            sx={{
                width: 200,
                mb: 0.6,
            }}
            alwaysOn
            resettable
        />

        <SelectInput
            source={'currentNode.name'}
            name={'currentNode.name'}
            choices={map(
                [AUDIT_STATE_AUDIT, AUDIT_STATE_TAKE_INTO_ACCOUNT, AUDIT_STATE_CANCELED, AUDIT_STATE_DONE],
                (v) => ({ name: v, id: v }),
            )}
            label='intervention.filters.status'
            sx={{
                width: 200,
            }}
            alwaysOn
            resettable
        />
    </FilterBox>,
];

export default AuditsList;
